import { createStructuredSelector } from "reselect";

import type { PollService } from "@busbud/core-types";

import type {
  CheckoutCommonReduxState,
  CheckoutReduxStateV2
} from "@app/types/checkout-v2-types";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { SearchInfo } from "@app/types/search-types";

export const getAmenitiesFilter = (state: ResultsReduxState) =>
  state.filters.amenities;
export const getCart = (state: ResultsReduxState) => state.cart;
export const getAsyncCart = (state: ResultsReduxState) => state.cart?.cart;
export const getConfig = (state: ResultsReduxState) => state.config;
export const getPaymentType = (state: ResultsReduxState) => state.payment_type;
export const getCartPurchase = (state: ResultsReduxState) =>
  state.cart_purchase;
export const getInventoryReady = (state: ResultsReduxState) =>
  state.inventory_ready;
export const getExperiments = (state: ResultsReduxState) =>
  state.config.experiments;
export const getInitialFormValues = (state: ResultsReduxState) =>
  state.cart_form_values;
export const getLocationFilters = createStructuredSelector<
  ResultsReduxState,
  { origin: number[]; destination: number[] }
>({
  origin: state => state.filters.origin_location,
  destination: state => state.filters.destination_location
});
export const getSearch = (state: ResultsReduxState): SearchInfo => state.search;
export const getTrips = (
  state: ResultsReduxState
):
  | Record<string, PollService.API.RelatedPublicTrip>
  | Record<string, PollService.API.RelatedPublicTripV4> => state.trips;
export const getTripsSegments = (
  state: ResultsReduxState
):
  | Record<string, PollService.API.RelatedPublicSegment>
  | Record<string, PollService.API.RelatedPublicSegmentV4> =>
  state.trips_segments;
export const getTripsFares = (state: ResultsReduxState) => state.trips_fares;
export const getTripsAnnotations = (state: ResultsReduxState) =>
  state.trips_annotations;
export const getUserCountryCode = (state: ResultsReduxState) =>
  state.config.user_country;
export const getUserRegion = (state: ResultsReduxState) =>
  state.config.user_region;
export const getLocale = (state: ResultsReduxState) => state.config.locale;
export const getVehicleLayoutTypeFilter = (state: ResultsReduxState) =>
  state.filters.vehicle_layout_type;
export const getMaxPriceFilter = (state: ResultsReduxState) =>
  state.filters.max_price[0] || null;
export const getExcludedSourcesFilter = (state: ResultsReduxState) =>
  state.filters.excluded_sources;
export const getSortingKey = (state: ResultsReduxState) => state.sorting;
export const getSources = (state: ResultsReduxState) => state.sources;
export const getTripStopsFetchStatus = (state: ResultsReduxState) =>
  state.trip_stops_fetch_status;
export const getTripsCities = (
  state: ResultsReduxState | CheckoutCommonReduxState
) => state.trips_cities;
export const getTripsLocations = (
  state: ResultsReduxState | CheckoutCommonReduxState
) => state.trips_locations;
export const getTripsOperators = (state: ResultsReduxState) =>
  state.trips_operators;
export const getFilters = (state: ResultsReduxState) => state.filters;
export const getOperatorProfileRatings = (state: ResultsReduxState) =>
  state.operator_profile_ratings;
export const getOperatorProfileReviews = (state: ResultsReduxState) =>
  state.operator_profile_reviews;
export const getSavedTravellers = (state: ResultsReduxState) =>
  state.saved_travellers;
export const getUser = (state: ResultsReduxState) => state.user;
export const getPage = (state: CheckoutReduxStateV2 | ResultsReduxState) =>
  state.page;
export const getSearchedRoutes = (state: ResultsReduxState) =>
  state.searched_routes;
export const getRoute = (state: ResultsReduxState) => state.route;
export const getSearchReconciliation = (state: ResultsReduxState) => {
  return state.search_reconciliation;
};
export const getTripDetailsFetchStatus = (state: ResultsReduxState) =>
  state.trip_details_fetch_status;
export const getRouteOperators = (state: ResultsReduxState) =>
  state.route.operators;
export const getSearchOriginName = (state: ResultsReduxState) =>
  state.route.origin_geo_entity?.name ?? state.route.origin_city?.name;
export const getSearchDestinationName = (state: ResultsReduxState) =>
  state.route.destination_geo_entity?.name ??
  state.route.destination_city?.name;
export const getSearchDestinationId = (state: ResultsReduxState) =>
  state.route.destination_geo_entity?.id ?? state.route.destination_city?.id;
export const getSearchDestinationHeroImageUrl = (state: ResultsReduxState) =>
  state.route.destination_city?.hero_image_url;

export const getPurchaseProgress = (state: ResultsReduxState) =>
  state.purchase_progress;
export const getAlternativeRoutes = (state: ResultsReduxState) =>
  state.alternative_routes;
export const getTicketTypes = (state: ResultsReduxState) => state.ticket_types;
export const isDropinWaitingUserAction = (state: ResultsReduxState) => {
  return state.is_dropin_waiting_user_action;
};
